import type { ReactNode } from 'react';

import { ATLAS_CELL, ATLAS_SIZE, ATLAS_URL, atlasFrame } from '@/game/data/itemAtlas';
import { Icon } from '@/components/ui/Icon';

/**
 * An item's icon: the Blender-rendered 3D art from the sprite atlas.
 *
 * Falls back to a drawn glyph when the atlas has no frame for the key. That
 * path is not decoration — a new item seeded before its model is rendered
 * would otherwise draw whatever happens to sit at (0, 0) in the sheet, which
 * looks like a bug rather than a missing asset.
 *
 * Drawn with `background-position` on one shared image rather than 50 `<img>`
 * tags: the browser decodes the sheet once, and a bag full of items costs one
 * request instead of fifty. On a 3G connection that difference dwarfs the bytes.
 */
export function ItemIcon({ itemKey, fallback, size = 40, className = '', alt }: {
  itemKey: string;
  /** Shown when the atlas has no art for this item. */
  fallback?: ReactNode;
  size?: number;
  className?: string;
  /** Omit for decorative use; the item name is usually already in the row. */
  alt?: string;
}) {
  const frame = atlasFrame(itemKey);

  if (!frame) {
    return (
      <span
        className={`inline-grid place-items-center text-ink-dim ${className}`}
        style={{ width: size, height: size }}
        role={alt ? 'img' : undefined}
        aria-label={alt}
        aria-hidden={alt ? undefined : true}
      >
        {fallback ?? <Icon name="bag" size={size * 0.6} />}
      </span>
    );
  }

  /* The sheet is scaled as a whole, so each cell lands at `size`. */
  const k = size / ATLAS_CELL;

  return (
    <span
      className={`inline-block shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        backgroundImage: `url(${ATLAS_URL})`,
        backgroundPosition: `-${frame.x * k}px -${frame.y * k}px`,
        backgroundSize: `${ATLAS_SIZE.width * k}px ${ATLAS_SIZE.height * k}px`,
        backgroundRepeat: 'no-repeat',
        /* Renders are soft-edged 3D, not pixel art — keep smooth scaling. */
        imageRendering: 'auto',
      }}
      role={alt ? 'img' : undefined}
      aria-label={alt}
      aria-hidden={alt ? undefined : true}
    />
  );
}
